import React from 'react';
import { Button, Icon } from '@gerdau/hefesto';
import {
  EFabricationOrderStatus,
  IFabricationOrder,
  IFabricationOrderDelivery,
  fabricationOrderStatusMap
} from '../../../../../store/ducks/fabrication-orders/types';
import moment from 'moment';
import theme from '../../../../../assets/styles/theme/theme';
import { MarketingCloudService } from '../../../../../services/MarketingCloud';

type IExcelFabricationOrder = {
  delivery?: IFabricationOrderDelivery;
} & Omit<IFabricationOrder, 'delivery'>;

interface IExcelColumn {
  title: string;
  width: number;
}

interface IDownloadExcelButtonProps {
  cleanSelectedItens: () => void;
  fabricationOrders: IFabricationOrder[];
}

const DownloadExcelButton: React.FC<IDownloadExcelButtonProps> = ({
  fabricationOrders,
  cleanSelectedItens
}) => {
  const evenRowColor = theme.colors.white3;
  const headerBackgroundColorBlue = theme.colors.blue2;
  const deliveriesHeaderColor = theme.colors.yellow;

  const exportOrders = () => {
    let table = '<html xmlns:x="urn:schemas-microsoft-com:office:excel">';
    table =
      table +
      '<head><meta charset="UTF-8"><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>';
    table =
      table +
      '<x:WorksheetOptions><x:Panes></x:Panes></x:WorksheetOptions></x:ExcelWorksheet>';
    table = table + '</x:ExcelWorksheets></x:ExcelWorkbook></xml></head><body>';
    table = table + "<table border='1' bordercolor='white'><tbody>";

    table += getExcelContent();

    table = table + '</tbody></table></body></html>';
    downloadExcel(table);
    cleanSelectedItens();
    MarketingCloudService.sendEvent({
      type: 'fabrication-orders',
      downloadExcel: true
    });
  };

  const getExcelFabricationOrders = (): IExcelFabricationOrder[] => {
    const excelOrders: IExcelFabricationOrder[] = [];

    fabricationOrders.forEach((order) => {
      if (order.deliveries.length > 0) {
        order.deliveries.forEach((delivery) => {
          excelOrders.push({
            ...order,
            delivery
          });
        });
      } else {
        excelOrders.push(order);
      }
    });
    return excelOrders;
  };

  const getExcelContent = (): string => {
    const hasSomeDelivery = fabricationOrders.some(
      (order) => order.deliveries.length > 0
    );

    let fabricationOrderContent = '';

    const excelFabricationOrders: IExcelFabricationOrder[] =
      getExcelFabricationOrders();

    if (hasSomeDelivery) {
      fabricationOrderContent += `            
      <tr style="font-size:14px; font-weight:bold;">
        <th align="center" style="background: ${headerBackgroundColorBlue};" colspan="6"> </th>
        <th align="center" style="background: ${deliveriesHeaderColor};" colspan="5"> Entregas </th>
      </tr>
    `;
    }

    const ordersColumns: IExcelColumn[] = [
      { title: 'Código do pedido', width: 150 },
      { title: 'Contrato', width: 80 },
      { title: 'Status do pedido', width: 120 },
      { title: 'Data de envio', width: 100 },
      { title: 'Data desejada', width: 100 },
      { title: 'Peso total', width: 120 }
    ];

    fabricationOrderContent += `<tr style="font-size:14px; vertical-align:middle; white-space:pre-wrap">`;

    ordersColumns.forEach((oc) => {
      fabricationOrderContent += `
        <td align="center" style="background:${headerBackgroundColorBlue}; height:80px; width:${oc.width}px">${oc.title}</td>`;
    });

    const deliveryColumns: IExcelColumn[] = [
      { title: 'ID Entrega', width: 120 },
      { title: 'Etapa da obra entrega', width: 180 },
      { title: 'Descrição da entrega', width: 300 },
      { title: 'Data de entrega', width: 120 },
      { title: 'Peso total entrega', width: 130 }
    ];

    if (hasSomeDelivery) {
      deliveryColumns.forEach((dc) => {
        fabricationOrderContent += `
            <td align="center" style="background:${deliveriesHeaderColor}; height:80px; width:${dc.width}px">${dc.title}</td>`;
      });
    }

    fabricationOrderContent += `</tr>`;

    const getTd = (content: string | number, idx: number) => {
      return `<td style="white-space:nowrap; font-size:14px; ${
        idx & 1 ? `background:${evenRowColor}` : ''
      }">${content + ' '}</td>`;
    };

    excelFabricationOrders.forEach((order, idx) => {
      const orderTotalWeight = order.deliveries.reduce(
        (acc, curr) => (curr.totalWeight || 0) + acc,
        0
      );

      fabricationOrderContent += `<tr>`;
      fabricationOrderContent += getTd(order.orderNumber, idx);
      fabricationOrderContent += getTd(order.contractNumber, idx);
      fabricationOrderContent += getTd(
        fabricationOrderStatusMap
          .get(order.orderStatus.toString() as EFabricationOrderStatus)
          ?.toString() || '',
        idx
      );
      fabricationOrderContent += getTd(
        moment(order.sendDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        idx
      );
      fabricationOrderContent += getTd(
        moment(order.desiredDate, 'YYYY-MM-DD').format('DD/MM/YYYY'),
        idx
      );
      fabricationOrderContent += getTd(
        orderTotalWeight > 0
          ? orderTotalWeight.toFixed(2).toString().replace('.', ',') + ' T'
          : '-',
        idx
      );

      if (hasSomeDelivery) {
        fabricationOrderContent += getTd(
          order.delivery?.deliveryId ?? '-',
          idx
        );
        fabricationOrderContent += getTd(
          order.delivery?.constructionStep ?? '-',
          idx
        );
        fabricationOrderContent += getTd(
          order.delivery?.description ?? '-',
          idx
        );
        fabricationOrderContent += getTd(
          order.delivery
            ? moment(order.delivery.scheduledDate, 'YYYY-MM-DD').format(
                'DD/MM/YYYY'
              )
            : '-',
          idx
        );
        fabricationOrderContent += getTd(
          order.delivery
            ? order.delivery.totalWeight
                .toFixed(2)
                .toString()
                .replace('.', ',') + ' T'
            : '-',
          idx
        );
      }
    });

    return fabricationOrderContent;
  };

  const downloadExcel = (table: string) => {
    const today = moment().format('DD_MM_YYYY');
    const fileName = `Pedidos corte e dobra Gerdau - ${today}.xls`;
    const element = document.createElement('a');
    element.setAttribute(
      'href',
      'data:application/vnd.ms-excel,' + encodeURIComponent(table)
    );
    element.setAttribute('download', fileName);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Button
      name="download-excel"
      variant="secondary"
      icon={<Icon option="Download" />}
      iconPosition="left"
      disabled={fabricationOrders.length === 0}
      onClick={exportOrders}
    >
      Baixar Excel
    </Button>
  );
};

export default DownloadExcelButton;
